import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ATMOSPHERE } from '../data/products';
import Breadcrumbs from '../components/Breadcrumbs';
import ChapterBreak from '../components/ChapterBreak';

const MATERIALS = [
  { n:'01', k:'Aluminium composite shell', v:'Precision-formed exterior panels finished with premium 2K automotive paint. Dimensionally stable, low-maintenance and highly resistant to weathering.' },
  { n:'02', k:'Structural steel frame',    v:'Powder-coated, corrosion-protected steel engineered for long-term structural performance and repeatable factory alignment.' },
  { n:'03', k:'High-density PU insulation',v:'A continuous polyurethane thermal envelope. Superior R-value per millimetre and excellent acoustic damping.' },
  { n:'04', k:'Insulated double glazing',  v:'Floor-to-ceiling low-E double glazing. Maximises daylight while reducing thermal transfer and external noise.' },
  { n:'05', k:'SPC engineered flooring',   v:'Wood-inspired, waterproof stone-polymer composite floor. Warm underfoot, easy to maintain and dimensionally stable.' },
  { n:'06', k:'Curated interior finishes', v:'Warm timber tones, textured neutrals and hospitality-grade fittings, specified to feel considered rather than decorated.' },
];

export default function Materials() {
  return (
    <main data-testid="materials-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Materials & Finishes | MODULAROOM</title>
        <meta name="description" content="A curated material palette for luxury modular homes. Aluminium composite shell, insulated glazing, engineered timber and hospitality-grade finishes specified to age gracefully." />
        <link rel="canonical" href="https://modularoom.com/materials" />
      </Helmet>
      <section className="pt-40 md:pt-56 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Breadcrumbs items={[{label:'Home', to:'/'}, {label:'Design & Engineering'}, {label:'Materials'}]} />
        <h1 className="mt-14 font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
          A material palette,<br/><span className="italic text-black/40">carefully specified.</span>
        </h1>
        <p className="mt-10 max-w-xl text-[17px] text-black/60 leading-[1.75]">
          Every layer of a MODULAROOM, structural, envelope and interior, is selected to age gracefully and finish beautifully. Nothing generic. Nothing site-improvised.
        </p>
      </section>

      <ChapterBreak src={ATMOSPHERE.brandLight} chapter="Materials" minHeight="55svh" />

      <section className="bg-[#F5F5F5] py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {MATERIALS.map((m) => (
              <div key={m.k} className="border-t border-black/10 pt-6 flex gap-6">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] w-8 shrink-0">{m.n}</div>
                <div>
                  <div className="font-display text-2xl md:text-3xl tracking-tight">{m.k}</div>
                  <div className="mt-3 text-black/65 leading-relaxed max-w-lg">{m.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 relative aspect-[4/5] overflow-hidden">
            <img src={ATMOSPHERE.technology} alt="Aluminium shell detail" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Custom finishes</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">Configure to your site.</h2>
            <p className="mt-6 max-w-md text-black/65 leading-relaxed">
              Exterior paint tones, interior wood species, stone and textile palettes can be tailored to reflect the setting of your project, from vineyard to shoreline to mountain retreat.
            </p>
            <Link to="/contact#book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] transition-colors">Request material palette</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
